import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import {
  CustomerIdInfoDTO,
  UpdateCustomerIdInfoDTO,
} from "@/modules/property-tracks/dtos/customer-id-info";
import { useUpdateCustomerIdInfo } from "@/modules/property-tracks/hooks/customer-id-info/use-customer-id-info.hook";
import { Box, TextField, useTheme } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";

const customerIdInfoSchema = z.object({
  fullName: z.string().optional(),
  surname: z.string().optional(),
  givenNames: z.string().optional(),
  idNumber: z.string().optional(),
  nationality: z.string().optional(),
  issuingCountry: z.string().optional(),
  gender: z.string().optional(),
  religion: z.string().optional(),
  address: z.string().optional(),
  birthDate: z.string().optional(),
  expiryDate: z.string().optional(),
});

type CustomerIdInfoFormFields = z.infer<typeof customerIdInfoSchema>;

const TEXT_FIELDS: {
  name: keyof CustomerIdInfoFormFields;
  label: string;
  multiline?: boolean;
}[] = [
  { name: "fullName", label: "Full Name" },
  { name: "surname", label: "Surname" },
  { name: "givenNames", label: "Given Names" },
  { name: "idNumber", label: "ID Number" },
  { name: "nationality", label: "Nationality" },
  { name: "issuingCountry", label: "Issuing Country" },
  { name: "gender", label: "Gender" },
  { name: "religion", label: "Religion" },
  { name: "address", label: "Address", multiline: true },
];

const DATE_FIELDS: { name: keyof CustomerIdInfoFormFields; label: string }[] = [
  { name: "birthDate", label: "Birth Date" },
  { name: "expiryDate", label: "Expiry Date" },
];

const EditCustomerIdInfoModal = ({
  info,
  open,
  setOpen,
}: {
  info: CustomerIdInfoDTO;
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const mutation = useUpdateCustomerIdInfo();

  const defaultValues: CustomerIdInfoFormFields = {
    fullName: info.fullName ?? "",
    surname: info.surname ?? "",
    givenNames: info.givenNames ?? "",
    idNumber: info.idNumber ?? "",
    nationality: info.nationality ?? "",
    issuingCountry: info.issuingCountry ?? "",
    gender: info.gender ?? "",
    religion: info.religion ?? "",
    address: info.address ?? "",
    birthDate: info.birthDate ?? "",
    expiryDate: info.expiryDate ?? "",
  };

  const form = useForm<CustomerIdInfoFormFields>({
    resolver: zodResolver(customerIdInfoSchema),
    values: defaultValues,
  });

  const handleCloseModal = () => {
    setOpen(false);
    form.reset(defaultValues);
  };

  const onSubmit: SubmitHandler<CustomerIdInfoFormFields> = (data) => {
    const body: UpdateCustomerIdInfoDTO = {
      fullName: data.fullName,
      surname: data.surname,
      givenNames: data.givenNames,
      idNumber: data.idNumber,
      nationality: data.nationality,
      issuingCountry: data.issuingCountry,
      gender: data.gender,
      religion: data.religion,
      address: data.address,
      // Empty date strings must be sent as null — the backend rejects "" on date columns.
      birthDate: data.birthDate ? data.birthDate : null,
      expiryDate: data.expiryDate ? data.expiryDate : null,
    };

    mutation.mutate(
      { uid: info.uid, data: body },
      {
        onSuccess: () => {
          setOpen(false);
          showToast({
            type: ToastType.SUCCESS,
            message: "Customer ID info updated successfully",
            toastId: TOAST_IDS.UPDATE_CUSTOMER_ID_INFO_SUCCESS,
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.UPDATE_CUSTOMER_ID_INFO_ERROR,
          });
        },
      }
    );
  };

  const formFields = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
      }}
    >
      {TEXT_FIELDS.map(({ name, label, multiline }) => (
        <Controller
          key={name}
          control={form.control}
          name={name}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              label={label}
              fullWidth
              size="small"
              multiline={multiline}
              minRows={multiline ? 2 : undefined}
            />
          )}
        />
      ))}
      {DATE_FIELDS.map(({ name, label }) => (
        <Controller
          key={name}
          control={form.control}
          name={name}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              label={label}
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      ))}
    </Box>
  );

  if (isMobile) {
    return (
      <CommonBottomSheet
        isOpen={open}
        onClose={handleCloseModal}
        snapPoints={[0.8, 0.8]}
      >
        <ModalHeader title="Edit Customer ID Info" onClose={handleCloseModal} />
        <Box overflow="auto" width="100%" height="90%">
          <Box p={2}>{formFields}</Box>
          <Box position="sticky" bottom={0} width="100%">
            <ModalFooter
              confirmButtonText="Save"
              cancelButtonText="Cancel"
              onConfirm={form.handleSubmit(onSubmit)}
              isLoading={mutation.isPending}
              onCancel={handleCloseModal}
            />
          </Box>
        </Box>
      </CommonBottomSheet>
    );
  }

  return (
    <EditFieldModal
      open={open}
      setOpen={setOpen}
      header="Edit Customer ID Info"
      width={480}
      handleConfirm={form.handleSubmit(onSubmit)}
      handleCloseModal={handleCloseModal}
      isLoading={mutation.isPending}
    >
      {formFields}
    </EditFieldModal>
  );
};

export default EditCustomerIdInfoModal;
